import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/lib/api";

export default function GeoGrid() {
  const { activeBiz } = useOutletContext();
  const [keyword, setKeyword] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activeBiz) return;
    const first = (activeBiz.keywords || [])[0];
    if (first) { setKeyword(first); load(first); }
    // eslint-disable-next-line
  }, [activeBiz]);

  const load = async (kw) => {
    if (!activeBiz || !kw) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/businesses/${activeBiz.id}/grid`, { params: { keyword: kw, size: 5, radius: 3 } });
      setData(data);
    } catch {}
    setLoading(false);
  };

  if (!activeBiz) return <div className="p-12 text-zinc-500">Add a business first.</div>;

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-6xl">
      <header>
        <div className="label-tiny mb-1">Geo-Grid</div>
        <h1 className="font-display text-4xl font-black tracking-tighter" data-testid="geogrid-title">25-Point Coverage</h1>
        <p className="text-zinc-500 mt-1">3-mile radius around your business. Green = Top 3.</p>
      </header>

      <div className="flex flex-wrap gap-2 items-center">
        <span className="label-tiny">Keyword:</span>
        {(activeBiz.keywords || []).map((k) => (
          <button
            key={k}
            onClick={() => { setKeyword(k); load(k); }}
            className={`px-3 py-1.5 text-xs font-mono border ${keyword === k ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-zinc-200 hover:border-zinc-400"}`}
            data-testid={`grid-kw-${k}`}
          >
            {k}
          </button>
        ))}
        {!(activeBiz.keywords || []).length && <span className="text-zinc-400 text-sm">No keywords. Add some in Settings.</span>}
      </div>

      {loading && <div className="py-12 flex justify-center"><span className="spinner text-zinc-400"></span></div>}

      {data && !loading && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bento p-6">
            <div className="label-tiny mb-3">"{data.keyword}" · 5×5 grid · {data.radius_mi}mi radius</div>
            <div className="grid grid-cols-5 gap-px bg-zinc-200" data-testid="geo-grid-cells">
              {data.points.map((p, i) => {
                const cls = p.rank <= 3 ? "bg-[#22C55E]" : p.rank <= 10 ? "bg-[#EAB308]" : "bg-[#EF4444]";
                return (
                  <div key={i} className={`grid-cell ${cls}`} title={`(${p.lat.toFixed(3)}, ${p.lng.toFixed(3)}) #${p.rank}`}>
                    {p.rank}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="space-y-4">
            <div className="bento p-5">
              <div className="label-tiny">Top-3 Coverage</div>
              <div className="font-display text-5xl font-black mt-2 text-[#22C55E]" data-testid="grid-top3-pct">{data.top_3_pct}%</div>
              <div className="text-xs text-zinc-500 mt-1 font-mono">of 25 points in Top 3</div>
            </div>
            <div className="bento p-5">
              <div className="label-tiny">Average Rank</div>
              <div className="font-display text-5xl font-black mt-2" data-testid="grid-avg-rank">#{data.avg_rank}</div>
              <div className="text-xs text-zinc-500 mt-1 font-mono">across the grid</div>
            </div>
            <div className="bento p-5">
              <div className="label-tiny mb-2">Legend</div>
              <div className="flex items-center gap-2 text-xs"><span className="w-3 h-3 bg-[#22C55E]"></span> Top 3 (winning)</div>
              <div className="flex items-center gap-2 text-xs mt-1"><span className="w-3 h-3 bg-[#EAB308]"></span> Top 10 (close)</div>
              <div className="flex items-center gap-2 text-xs mt-1"><span className="w-3 h-3 bg-[#EF4444]"></span> Push harder</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
